import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/auth/AuthContext";
import HeaderBar from "@/auth/HeaderBar";
import Footer from "@/components/landing/Footer";
import { Button } from "@/components/ui/button";
import { Lock, Loader2, CheckCircle2, AlertTriangle, Calendar, DollarSign } from "lucide-react";

function fmtMoney(v) {
    try {
        return `$${Number(v).toFixed(2)}`;
    } catch {
        return "—";
    }
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-US", {
            weekday: "long",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
            timeZoneName: "short",
        });
    } catch {
        return iso;
    }
}

function fmtDeliveryDate(iso) {
    if (!iso) return "—";
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-US", {
        weekday: "long",
        month: "short",
        day: "numeric",
    });
}

export default function PayBalancePage() {
    const { order_id } = useParams();
    const [search] = useSearchParams();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [paying, setPaying] = useState(false);
    const [error, setError] = useState("");
    const justPaid = search.get("paid") === "1";
    const canceled = search.get("canceled") === "1";
    const statusSession = search.get("session_id");

    const load = React.useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/orders/${order_id}/balance`);
            setOrder(res.data);
        } catch (err) {
            setError(err?.response?.data?.detail || "Order not found");
        } finally {
            setLoading(false);
        }
    }, [order_id]);

    useEffect(() => {
        load();
    }, [load]);

    // After Stripe returns ?paid=1&session_id=... poll the checkout/status
    // endpoint so the backend marks the balance as paid (idempotent).
    useEffect(() => {
        if (!justPaid || !statusSession) return;
        let cancelled = false;
        let attempts = 0;
        async function poll() {
            attempts += 1;
            try {
                await axios.get(`${API}/checkout/status/${statusSession}`);
                await load();
                if (cancelled) return;
                if (attempts < 6 && (order?.balance_status !== "paid")) {
                    setTimeout(poll, 2000);
                }
            } catch {
                /* swallow */
            }
        }
        poll();
        return () => {
            cancelled = true;
        };
    }, [justPaid, statusSession, load, order?.balance_status]);

    const startBalancePay = async () => {
        setPaying(true);
        setError("");
        try {
            const res = await axios.post(
                `${API}/orders/${order_id}/balance/session`,
                { origin_url: window.location.origin },
            );
            if (res.data?.session_url) {
                window.location.href = res.data.session_url;
                return;
            }
            setError("Could not start checkout. Please try again.");
        } catch (err) {
            setError(err?.response?.data?.detail || "Could not start checkout");
        } finally {
            setPaying(false);
        }
    };

    return (
        <div className="min-h-screen bg-cream">
            <HeaderBar />
            <div className="max-w-3xl mx-auto px-6 sm:px-8 pt-10 pb-20">
                <Link
                    to="/"
                    className="inline-flex items-center gap-1 text-sm font-bold text-forest hover:text-forestDark mb-4"
                >
                    ← Back to home
                </Link>
                <h1 className="font-serif font-black text-3xl sm:text-4xl text-navy">
                    Pay your pre-order balance
                </h1>
                <p className="mt-2 text-muted2">
                    Order <span className="font-mono">#{(order_id || "").slice(0, 8).toUpperCase()}</span>
                </p>

                {loading && (
                    <div className="mt-10 flex items-center gap-2 text-muted2">
                        <Loader2 className="animate-spin" size={18} /> Loading…
                    </div>
                )}

                {!loading && error && (
                    <div className="mt-10 bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgba(0,0,0,0.06)] border border-burnt/20">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="text-burnt mt-0.5" size={20} />
                            <div>
                                <p className="font-bold text-navy">
                                    We couldn't find that pre-order
                                </p>
                                <p className="text-sm text-muted2 mt-1">
                                    {error}. If you think this is a mistake,
                                    reply to your confirmation email and we'll
                                    look it up.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {!loading && order && !error && (
                    <div className="mt-8 space-y-5">
                        {/* Paid confirmation */}
                        {order.balance_status === "paid" && (
                            <div
                                data-testid="balance-paid-banner"
                                className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgba(0,0,0,0.06)] border-2 border-forest"
                            >
                                <div className="flex items-start gap-3">
                                    <CheckCircle2 className="text-forest mt-0.5" size={28} />
                                    <div>
                                        <p className="font-serif font-black text-2xl text-navy">
                                            You're locked in! 🇭🇹
                                        </p>
                                        <p className="text-sm text-navy mt-1">
                                            Your balance is paid in full. We'll see
                                            you on <b>{fmtDeliveryDate(order.delivery_date)}</b>.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Forfeit / canceled */}
                        {order.balance_status === "forfeit" && (
                            <div className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgba(0,0,0,0.06)] border-2 border-burnt">
                                <div className="flex items-start gap-3">
                                    <AlertTriangle className="text-burnt mt-0.5" size={20} />
                                    <div>
                                        <p className="font-bold text-navy">
                                            This pre-order was canceled
                                        </p>
                                        <p className="text-sm text-muted2 mt-1">
                                            The balance deadline passed without
                                            payment. Per our Refund Policy, the
                                            deposit is non-refundable on
                                            non-payment cancellations. Reply to
                                            your confirmation email if you'd like
                                            to start a fresh order.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Active balance due */}
                        {order.balance_status === "due" && (
                            <>
                                {canceled && (
                                    <div className="bg-white rounded-2xl p-4 shadow-[0_4px_18px_rgba(0,0,0,0.06)] border border-mint">
                                        <p className="text-sm text-burnt font-bold">
                                            Checkout canceled. Your balance is still due — tap below to try again.
                                        </p>
                                    </div>
                                )}

                                <div
                                    data-testid="balance-summary-card"
                                    className="bg-white rounded-3xl p-6 sm:p-8 shadow-[0_10px_38px_rgba(0,0,0,0.08)]"
                                >
                                    <div className="grid sm:grid-cols-2 gap-5">
                                        <div className="rounded-2xl bg-mint/40 p-4">
                                            <p className="text-[11px] uppercase tracking-widest font-bold text-forest">
                                                Deposit paid
                                            </p>
                                            <p className="font-display text-3xl text-forest mt-1 tabular-nums">
                                                {fmtMoney(order.deposit_amount)}
                                            </p>
                                        </div>
                                        <div className="rounded-2xl bg-gold/20 border-2 border-gold p-4">
                                            <p className="text-[11px] uppercase tracking-widest font-bold text-burnt">
                                                Balance due now
                                            </p>
                                            <p
                                                data-testid="balance-amount"
                                                className="font-display text-3xl text-burnt mt-1 tabular-nums"
                                            >
                                                {fmtMoney(order.balance_amount)}
                                            </p>
                                        </div>
                                    </div>

                                    <ul className="mt-5 space-y-2 text-sm text-navy">
                                        <li className="flex items-center gap-2">
                                            <Calendar size={14} className="text-forest" />
                                            <span>
                                                Delivery:{" "}
                                                <b>{fmtDeliveryDate(order.delivery_date)}</b>
                                            </span>
                                        </li>
                                        <li className="flex items-center gap-2">
                                            <DollarSign size={14} className="text-forest" />
                                            <span>
                                                Order total:{" "}
                                                <b className="tabular-nums">
                                                    {fmtMoney(order.total)}
                                                </b>
                                            </span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <AlertTriangle size={14} className="text-burnt mt-0.5" />
                                            <span>
                                                Balance must be paid by{" "}
                                                <b>{fmtDate(order.balance_due_at)}</b>.
                                                Non-payment forfeits the deposit (per Refund Policy).
                                            </span>
                                        </li>
                                    </ul>

                                    {error && (
                                        <p className="mt-4 text-sm text-burnt">{error}</p>
                                    )}

                                    <Button
                                        data-testid="pay-balance-cta"
                                        onClick={startBalancePay}
                                        disabled={paying}
                                        className="mt-6 w-full sm:w-auto rounded-full bg-gold hover:bg-goldDark text-navy font-bold px-8 py-6 text-base disabled:opacity-50"
                                    >
                                        <Lock size={14} className="mr-2" />
                                        {paying
                                            ? "Redirecting…"
                                            : `Pay ${fmtMoney(order.balance_amount)} balance`}
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
            <Footer />
        </div>
    );
}
